import { MEASUREMENT_SYSTEM_CHANGED_EVENT } from 'services/StudioEvents';

// These are legacy, and are only present 
const TEMPERATURE_UNIT_STORAGE_KEY = 'temperature-unit';
const TEMPERATURE_UNIT_FAHRENHEIT = 'fahrenheit';
const TEMPERATURE_UNIT_CELSIUS = 'celsius';

const MEASUREMENT_SYSTEM_STORAGE_KEY = 'measurement-system';
export const MEASUREMENT_SYSTEM_METRIC = 'metric';
export const MEASUREMENT_SYSTEM_IMPERIAL = 'imperial';

// Separate from the measurement system, as e.g. US hospitals mix Celsius
// with inches of water column.
const DIFFERENTIAL_PRESSURE_UNIT_STORAGE_KEY = 'differential-pressure-unit';
export const DIFFERENTIAL_PRESSURE_UNIT_PASCAL = 'pascal';
export const DIFFERENTIAL_PRESSURE_UNIT_INCHES_OF_WATER = 'inches-of-water';

const CANADIAN_TIME_ZONES = [
    "America/Cambridge_Bay",
    "America/Dawson",
    "America/Dawson_Creek",
    "America/Edmonton",
    "America/Fort_Nelson",
    "America/Glace_Bay",
    "America/Goose_Bay",
    "Canada/Atlantic",
    "America/Inuvik",
    "America/Pangnirtung",
    "America/Moncton",
    "America/Rankin_Inlet",
    "Canada/Saskatchewan",
    "America/Resolute",
    "Canada/Newfoundland",
    "America/Swift_Current",
    "America/Montreal",
    "America/Nassau",
    "America/Nipigon",
    "America/Thunder_Bay",
    "Canada/Eastern",
    "Canada/Pacific",
    "Canada/Yukon",
    "America/Rainy_River",
    "Canada/Central"
];


/* @ngInject */
export default class UserPreferencesManager {
    constructor(StateService, $q, StudioProfileService, ToastService, $rootScope, $exceptionHandler) {
        this.StateService = StateService;
        this.$q = $q;
        this.$exceptionHandler = $exceptionHandler;
        this.StudioProfileService = StudioProfileService;
        this.ToastService = ToastService;
        this.$rootScope = $rootScope;
    }

    getMeasurementSystem() {
        // Check first if there is a measurement system set for the Studio profile
        let measurementSystem = this.StudioProfileService.get(MEASUREMENT_SYSTEM_STORAGE_KEY);
        if (measurementSystem) {
            return measurementSystem;
        }

        // If no measurement system is set, check if there's a temperature unit 
        // specified in localStorage. If not, default to celsius.
        const tempUnit = this.StateService.getItem(TEMPERATURE_UNIT_STORAGE_KEY, TEMPERATURE_UNIT_CELSIUS);

        // Try to get the location from the browser to determine the measurement system
        let timeZone = null;
        try {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            console.error('Error getting time zone', e); // eslint-disable-line no-console
        }

        // Use the temperature unit and country code to determine the measurement system
        if (tempUnit === TEMPERATURE_UNIT_FAHRENHEIT ||
            (timeZone && timeZone.startsWith('America') && !CANADIAN_TIME_ZONES.includes(timeZone))) {
            measurementSystem = MEASUREMENT_SYSTEM_IMPERIAL;
        } else {
            measurementSystem = MEASUREMENT_SYSTEM_METRIC; // Default to metric
        }

        return measurementSystem;
    }


    setMeasurementSystem(measurementSystem, broadcastChange = true) {
        return this.StudioProfileService.set(MEASUREMENT_SYSTEM_STORAGE_KEY, measurementSystem)
            .then(() => {
                if (broadcastChange) {
                    // TODO: Change this to a measurement system toast when UI shows measurement system
                    this.ToastService.showSimpleTranslated('temperature_unit_saved');
                    this.$rootScope.$broadcast(MEASUREMENT_SYSTEM_CHANGED_EVENT);
                }
            })
            .catch((e) => {
                this.$exceptionHandler(e);
                if (broadcastChange) {
                    // TODO: Change this to a measurement system toast when UI shows measurement system
                    this.ToastService.showSimpleTranslated('temperature_unit_not_saved');
                }
            });
    }

    getDifferentialPressureUnit() {
        return this.StudioProfileService.get(DIFFERENTIAL_PRESSURE_UNIT_STORAGE_KEY) || DIFFERENTIAL_PRESSURE_UNIT_PASCAL;
    }

    setDifferentialPressureUnit(differentialPressureUnit) {
        return this.StudioProfileService.set(DIFFERENTIAL_PRESSURE_UNIT_STORAGE_KEY, differentialPressureUnit)
            .then(() => {
                this.ToastService.showSimpleTranslated('differential_pressure_unit_saved');
                this.$rootScope.$broadcast(MEASUREMENT_SYSTEM_CHANGED_EVENT);
            })
            .catch((e) => {
                this.$exceptionHandler(e);
                this.ToastService.showSimpleTranslated('temperature_unit_not_saved');
            });
    }

    // Refreshes the local cache of user preferences, and broadcasts a 
    // MEASUREMENT_SYSTEM_CHANGED_EVENT event if the temperature or differential pressure unit has changed.
    refresh() {
        const originalMeasurementSystem = this.StudioProfileService.get(MEASUREMENT_SYSTEM_STORAGE_KEY);
        const originalDifferentialPressureUnit = this.StudioProfileService.get(DIFFERENTIAL_PRESSURE_UNIT_STORAGE_KEY);

        return this.StudioProfileService.refresh()
            .then(() => {
                const updatedMeasurementSystem = this.StudioProfileService.get(MEASUREMENT_SYSTEM_STORAGE_KEY);
                const updatedDifferentialPressureUnit = this.StudioProfileService.get(DIFFERENTIAL_PRESSURE_UNIT_STORAGE_KEY);

                if (originalMeasurementSystem !== updatedMeasurementSystem || originalDifferentialPressureUnit !== updatedDifferentialPressureUnit) {
                    this.$rootScope.$broadcast(MEASUREMENT_SYSTEM_CHANGED_EVENT);
                }
            })
    }

    get useFahrenheit() {
        return this.getMeasurementSystem() === MEASUREMENT_SYSTEM_IMPERIAL;
    }

    get useInchesOfWater() {
        return this.getDifferentialPressureUnit() === DIFFERENTIAL_PRESSURE_UNIT_INCHES_OF_WATER;
    }

    get use24HourClock() {
        return this.getMeasurementSystem() === MEASUREMENT_SYSTEM_METRIC;
    }
}
